import React, { useState } from "react";
import styled from "styled-components";
import { Plus, X } from "lucide-react";
import { allParameters } from "../data/mockData";
import TemplateConfirmationModal from "../Modal/TemplateConfirmationModal";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 500px;
  height: 680px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #E5E7EB;
  position: relative;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  flex: 1;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9CA3AF;
  padding: 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  right: 0;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const ContentSection = styled.div`
  padding: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagContainer = styled.div`
  position: relative;
`;

const TagInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const TagInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const AddTagButton = styled.button`
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  color: #374151;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00B5B8;
  color: #00B5B8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const TagRemoveButton = styled.button`
  background: white;
  border: 1px solid #00B5B8;
  cursor: pointer;
  color: #00B5B8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const GenderSection = styled.div`
  margin-bottom: 1rem;
`;

const GenderAgeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
`;

const GenderColumn = styled.div`
  flex: 1;
`;

const AgeColumn = styled.div`
  flex: 1;
`;

const GenderOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.4rem;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const RadioInput = styled.input`
  width: 0.9rem;
  height: 0.9rem;
  accent-color: #1D87F0;
  
  &:checked {
    background-color: #1D87F0;
    border-color: #1D87F0;
  }
`;

const RadioLabel = styled.label`
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
`;

const AgeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.4rem;
`;

const DualRangeContainer = styled.div`
  width: 140px;
  position: relative;
  height: 4px;
`;

const RangeTrack = styled.div`
  width: 100%;
  height: 4px;
  background: #E5E7EB;
  border-radius: 2px;
  position: relative;
`;

const RangeProgress = styled.div`
  height: 100%;
  background: #1D87F0;
  border-radius: 2px;
  position: absolute;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
`;

const RangeInput = styled.input`
  position: absolute;
  width: 100%;
  height: 4px;
  background: transparent;
  pointer-events: none;
  appearance: none;
  outline: none;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    pointer-events: all;
    position: relative;
    z-index: 2;
    margin-top: -5px;
    transform: translateY(-0.5px);
  }
  
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    border: none;
    pointer-events: all;
    margin-top: -5px;
  }
`;

const AgeDisplay = styled.div`
  background-color: #1D87F0;
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  min-width: 60px;
  text-align: center;
`;

const MultiSelectContainer = styled.div`
  position: relative;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`;

const MultiSelectWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MultiSelectButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  height: fit-content;
  
  &:hover {
    background-color: #0891B2;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid #E5E7EB;
  font-size: 0.8rem;
  
  &:focus {
    outline: none;
  }
`;

const ParameterOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

const Checkbox = styled.input`
  width: 0.8rem;
  height: 0.8rem;
  accent-color: #00B5B8;
`;

const TableContainer = styled.div`
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.5rem;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
`;

const TableCell = styled.td`
  padding: 0.5rem;
  font-size: 0.8rem;
  color: #374151;
`;

const TableRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TableDualRangeContainer = styled.div`
  width: 100px;
  position: relative;
  height: 3px;
`;

const TableRangeTrack = styled.div`
  width: 100%;
  height: 3px;
  background: #E5E7EB;
  border-radius: 1.5px;
  position: relative;
`;

const TableRangeProgress = styled.div`
  height: 100%;
  background: #1D87F0;
  border-radius: 1.5px;
  position: absolute;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
`;

const TableRangeInput = styled.input`
  position: absolute;
  width: 100%;
  height: 3px;
  background: transparent;
  pointer-events: none;
  appearance: none;
  outline: none;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    pointer-events: all;
    position: relative;
    z-index: 2;
    margin-top: -3.5px;
    transform: translateY(-0.5px);
  }
  
  &::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    border: none;
    pointer-events: all;
    margin-top: -3.5px;
  }
`;

const TableRangeDisplay = styled.div`
  font-size: 0.7rem;
  color: #6B7280;
  min-width: 50px;
  text-align: center;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #E5E7EB;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  
  &:hover {
    background-color: #F9FAFB;
  }
`;

const SaveButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  
  &:hover {
    background-color: #0891B2;
  }
`;

const CreateTemplateModal = ({ isOpen, onClose, onSave }) => {
  const [templateName, setTemplateName] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState(["ผู้สูงอายุ", "พันธุกรรม"]);
  const [selectedGender, setSelectedGender] = useState("male");
  const [ageRange, setAgeRange] = useState([25, 65]);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [tempSelectedParameters, setTempSelectedParameters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [parameterRanges, setParameterRanges] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);


  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleMinAgeChange = (e) => {
    const value = parseInt(e.target.value);
    setAgeRange([value, Math.max(value, ageRange[1])]);
  };

  const handleMaxAgeChange = (e) => {
    const value = parseInt(e.target.value);
    setAgeRange([Math.min(ageRange[0], value), value]);
  };

  const getProgressLeft = () => (ageRange[0] / 100) * 100;
  const getProgressWidth = () => ((ageRange[1] - ageRange[0]) / 100) * 100;

  const filteredParameters = allParameters.filter(param =>
    param.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleParameterToggle = (parameter) => {
    const isSelected = tempSelectedParameters.some(p => p.name === parameter.name);
    if (isSelected) {
      setTempSelectedParameters(tempSelectedParameters.filter(p => p.name !== parameter.name));
    } else {
      setTempSelectedParameters([...tempSelectedParameters, parameter]);
    }
  };

  const handleSelectParameters = () => {
    setSelectedParameters([...tempSelectedParameters]);
    
    const newRanges = { ...parameterRanges };
    
    Object.keys(newRanges).forEach(paramName => {
      if (!tempSelectedParameters.some(p => p.name === paramName)) {
        delete newRanges[paramName];
      }
    });
    
    tempSelectedParameters.forEach(parameter => {
      if (!newRanges[parameter.name]) {
        newRanges[parameter.name] = parameter.defaultRange;
      }
    });
    
    setParameterRanges(newRanges);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleDropdownOpen = () => {
    setTempSelectedParameters([...selectedParameters]);
    setIsDropdownOpen(true);
  };

  const handleParameterRangeChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || [0, 100];
    const newRange = isMin 
      ? [parseInt(value), Math.max(parseInt(value), currentRange[1])]
      : [Math.min(currentRange[0], parseInt(value)), parseInt(value)];
    
    setParameterRanges({
      ...parameterRanges,
      [paramName]: newRange
    });
  };

  const getTableProgressLeft = (paramName, maxValue) => {
    const range = parameterRanges[paramName] || [0, maxValue];
    return (range[0] / maxValue) * 100;
  };

  const getTableProgressWidth = (paramName, maxValue) => {
    const range = parameterRanges[paramName] || [0, maxValue];
    return ((range[1] - range[0]) / maxValue) * 100;
  };

  const handleSave = () => {
    // แสดง confirmation modal
    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    const newTemplate = {
      templateName,
      createdBy,
      tags,
      gender: selectedGender,
      ageRange,
      selectedParameters,
      parameterRanges
    };
    onSave(newTemplate);
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>สร้าง Template</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentSection>
            <FormGroup>
              <Label>ชื่อ Template</Label>
              <Input
                type="text"
                placeholder="ชื่อ Template"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ชื่อผู้สร้าง</Label>
              <Input
                type="text"
                placeholder="ชื่อผู้สร้าง"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ป้ายกำกับ (Tag)</Label>
              <TagContainer>
                <TagInputContainer>
                  <TagInput
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addTag();
                      }
                    }}
                  />
                  <AddTagButton onClick={addTag}>เพิ่ม</AddTagButton>
                </TagInputContainer>
                
                <TagList>
                  {tags.map((tag, index) => (
                    <Tag key={index}>
                      <span>{tag}</span>
                      <TagRemoveButton onClick={() => removeTag(tag)}>
                        <X size={8} />
                      </TagRemoveButton>
                    </Tag>
                  ))}
                </TagList>
              </TagContainer>
            </FormGroup>

            <GenderSection>
              <GenderAgeRow>
                <GenderColumn>
                  <Label>สำหรับเพศ</Label>
                  <GenderOptions>
                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={selectedGender === "male"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="male">ชาย</RadioLabel>
                    </RadioContainer>
                    
                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={selectedGender === "female"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="female">หญิง</RadioLabel>
                    </RadioContainer>
                    
                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="both"
                        name="gender"
                        value="both"
                        checked={selectedGender === "both"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="both">ทุกเพศ</RadioLabel>
                    </RadioContainer>
                  </GenderOptions>
                </GenderColumn>

                <AgeColumn>
                  <Label>ช่วงอายุ (ปี)</Label>
                  <AgeRangeContainer>
                    <DualRangeContainer>
                      <RangeTrack>
                        <RangeProgress 
                          left={getProgressLeft()} 
                          width={getProgressWidth()} 
                        />
                      </RangeTrack>
                      <RangeInput
                        type="range"
                        min="0"
                        max="100"
                        value={ageRange[0]}
                        onChange={handleMinAgeChange}
                      />
                      <RangeInput
                        type="range"
                        min="0"
                        max="100"
                        value={ageRange[1]}
                        onChange={handleMaxAgeChange}
                      />
                    </DualRangeContainer>
                    <AgeDisplay>{ageRange[0]}-{ageRange[1]}</AgeDisplay>
                  </AgeRangeContainer>
                </AgeColumn>
              </GenderAgeRow>
            </GenderSection>

            <FormGroup>
              <Label>เลือกพาราเมิเตอร์</Label>
              <MultiSelectContainer>
                <MultiSelectWrapper>
                  <MultiSelectButton 
                    type="button"
                    onClick={handleDropdownOpen}
                  >
                    <span>
                      {selectedParameters.length === 0 
                        ? 'เลือกพาราเมิเตอร์' 
                        : `${selectedParameters.length} selected`}
                    </span>
                    <span>{isDropdownOpen ? '▲' : '▼'}</span>
                  </MultiSelectButton>
                  
                  {isDropdownOpen && (
                    <DropdownContent>
                      <SearchInput
                        type="text"
                        placeholder="ค้นหาพาราเมิเตอร์..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {filteredParameters.map((parameter, index) => (
                        <ParameterOption key={index} onClick={() => handleParameterToggle(parameter)}>
                          <Checkbox
                            type="checkbox"
                            checked={tempSelectedParameters.some(p => p.name === parameter.name)}
                            onChange={() => {}}
                          />
                          <span>{parameter.name} ({parameter.unit})</span>
                        </ParameterOption>
                      ))}
                    </DropdownContent>
                  )}
                </MultiSelectWrapper>
                
                <SelectButton onClick={handleSelectParameters}>
                  เลือก
                </SelectButton>
              </MultiSelectContainer>
            </FormGroup>

            <TableContainer>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>พาราเมิเตอร์</TableHeaderCell>
                    <TableHeaderCell>ค่าปกติ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่กำหนด</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {selectedParameters.length > 0 ? (
                    selectedParameters.map((param, index) => {
                      const range = parameterRanges[param.name] || param.defaultRange;
                      const maxValue = param.normalRange[1];
                      return (
                        <TableRow key={index}>
                          <TableCell>{param.name}</TableCell>
                          <TableCell>{param.normalValue}</TableCell>
                          <TableCell>
                            <TableRangeContainer>
                              <TableDualRangeContainer>
                                <TableRangeTrack>
                                  <TableRangeProgress 
                                    left={getTableProgressLeft(param.name, maxValue)} 
                                    width={getTableProgressWidth(param.name, maxValue)} 
                                  />
                                </TableRangeTrack>
                                <TableRangeInput
                                  type="range"
                                  min={param.normalRange[0]}
                                  max={param.normalRange[1]}
                                  value={range[0]}
                                  onChange={(e) => handleParameterRangeChange(param.name, true, e.target.value)}
                                />
                                <TableRangeInput
                                  type="range"
                                  min={param.normalRange[0]}
                                  max={param.normalRange[1]}
                                  value={range[1]}
                                  onChange={(e) => handleParameterRangeChange(param.name, false, e.target.value)}
                                />
                              </TableDualRangeContainer>
                              <TableRangeDisplay>{range[0]}-{range[1]}</TableRangeDisplay>
                            </TableRangeContainer>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
          </ContentSection>

          <ActionSection>
            <CancelButton onClick={onClose}>ยกเลิก</CancelButton>
            <SaveButton onClick={handleSave}>สร้าง Template</SaveButton>
          </ActionSection>
        </ModalBody>
      </ModalContainer>

      <TemplateConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmSave}
      />
    </ModalOverlay>
  );
};

export default CreateTemplateModal;